/**
 * Drawings are pinned to TIME, not to bar positions. A bar position (lightweight-charts
 * "logical" index) changes whenever older candles are loaded on the left or the timeframe
 * changes, which made drawings slide off their candles. Each stored point keeps
 * `time` (unix seconds, may be fractional / beyond the last bar) and its `logical` index is
 * re-worked out from that time against the bars currently on the chart.
 */

const POINT_KEYS = ["start", "end", "fork", "entry", "stop", "target", "at"];

/** Typical bar spacing (median of the last gaps) — used past either end of the data. */
function typicalStep(times) {
  const gaps = [];
  for (let i = Math.max(1, times.length - 60); i < times.length; i += 1) {
    const g = times[i] - times[i - 1];
    if (g > 0) gaps.push(g);
  }
  if (!gaps.length) return 60;
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)];
}

/**
 * Converts between bar position and time for an ascending list of bar times (unix seconds).
 * Between two bars it interpolates (so weekend gaps are handled), past either end it uses the
 * typical bar spacing.
 */
export function makeTimeAxis(times) {
  const t = Array.isArray(times) ? times.filter((v) => Number.isFinite(v)) : [];
  const n = t.length;
  const step = typicalStep(t);
  const logicalToTime = (logical) => {
    if (!n || !Number.isFinite(logical)) return null;
    if (logical <= 0) return t[0] + logical * step;
    if (logical >= n - 1) return t[n - 1] + (logical - (n - 1)) * step;
    const i = Math.floor(logical);
    const f = logical - i;
    return t[i] + f * (t[i + 1] - t[i]);
  };
  const timeToLogical = (time) => {
    if (!n || !Number.isFinite(time)) return null;
    if (time <= t[0]) return (time - t[0]) / step;
    if (time >= t[n - 1]) return n - 1 + (time - t[n - 1]) / step;
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (t[mid] <= time) lo = mid;
      else hi = mid;
    }
    const span = t[hi] - t[lo];
    return lo + (span > 0 ? (time - t[lo]) / span : 0);
  };
  return { size: n, step, logicalToTime, timeToLogical };
}

/** Applies fn to every chart point of a drawing; returns a new drawing. */
export function mapDrawingPoints(drawing, fn) {
  if (!drawing) return drawing;
  let next = drawing;
  if (drawing.type === "vline") {
    const p = fn({ logical: drawing.logical, time: drawing.time, price: null });
    next = { ...next, logical: p.logical, time: p.time };
  }
  for (const key of POINT_KEYS) {
    if (drawing[key] && typeof drawing[key] === "object") {
      next = { ...next, [key]: fn(drawing[key]) };
    }
  }
  if (Array.isArray(drawing.points)) {
    next = { ...next, points: drawing.points.map(fn) };
  }
  return next;
}

/** Fills in `time` for points that do not have one yet (new or just moved). */
export function anchorDrawing(drawing, axis) {
  if (!axis || !axis.size) return drawing;
  return mapDrawingPoints(drawing, (p) =>
    Number.isFinite(p.time) || !Number.isFinite(p.logical) ? p : { ...p, time: axis.logicalToTime(p.logical) }
  );
}

/** Works out each point's bar position from its time against the bars now on the chart. */
export function resyncDrawing(drawing, axis) {
  if (!axis || !axis.size) return drawing;
  return mapDrawingPoints(drawing, (p) => {
    if (!Number.isFinite(p.time)) return p;
    const logical = axis.timeToLogical(p.time);
    return Number.isFinite(logical) && logical !== p.logical ? { ...p, logical } : p;
  });
}

/**
 * Magnet: snaps a price to the nearest open/high/low/close of a bar.
 * mode "strong" always snaps; "weak" only when within `pixels` on screen.
 * `toY` converts a price to a screen y. Returns { price, snapped }.
 */
export function magnetPrice(bar, price, y, toY, mode, pixels = 14) {
  if (!bar || mode === "off" || !mode) return { price, snapped: false };
  const values = ["open", "high", "low", "close", "value"]
    .map((k) => bar[k])
    .filter((v) => Number.isFinite(v));
  let best = null;
  let bestDist = Infinity;
  for (const v of values) {
    const vy = toY(v);
    if (!Number.isFinite(vy)) continue;
    const dist = Math.abs(vy - y);
    if (dist < bestDist) {
      bestDist = dist;
      best = v;
    }
  }
  if (best == null) return { price, snapped: false };
  if (mode === "strong" || bestDist <= pixels) return { price: best, snapped: true };
  return { price, snapped: false };
}

const STORAGE_PREFIX = "lwt.drawings.v1.";

/** Drawings saved for a symbol (empty list when nothing is saved or storage is blocked). */
export function loadSavedDrawings(symbol) {
  if (!symbol) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + symbol);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((d) => d && d.id && d.type) : [];
  } catch (e) {
    return [];
  }
}

export function saveDrawings(symbol, drawings) {
  if (!symbol) return;
  try {
    if (!drawings.length) window.localStorage.removeItem(STORAGE_PREFIX + symbol);
    else window.localStorage.setItem(STORAGE_PREFIX + symbol, JSON.stringify(drawings));
  } catch (e) {
    // storage full or blocked: drawings still work for this visit
  }
}
